import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import LoginPage from "./pages/LoginPage";
import PlayerDashboard from "./pages/PlayerDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import WalletPage from "./pages/WalletPage";
import SessionsPage from "./pages/SessionsPage";
import DecisionsPage from "./pages/DecisionsPage";
import FraudSignalsPage from "./pages/FraudSignalsPage";
import AgentLogsPage from "./pages/AgentLogsPage";
import CheckInPage from "./pages/CheckInPage";
import CoachDashboard from "./pages/CoachDashboard";
import VenueDashboard from "./pages/VenueDashboard";
import RedeemPage from "./pages/RedeemPage";
import PlannerPage from "./pages/PlannerPage";
import AppLayout from "./components/AppLayout";

function ProtectedRoute({ children, requiredRole, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020408] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (requiredRole && user.role !== requiredRole) return <Navigate to="/" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" replace />;

  return <AppLayout>{children}</AppLayout>;
}

function getDefaultRoute(role) {
  if (role === 'admin') return '/admin';
  return '/';
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020408] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to={getDefaultRoute(user.role)} replace /> : <LoginPage />}
      />

      {/* Universal Routes */}
      <Route path="/" element={<ProtectedRoute><PlayerDashboard /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
      <Route path="/sessions" element={<ProtectedRoute><SessionsPage /></ProtectedRoute>} />
      <Route path="/checkin" element={<ProtectedRoute><CheckInPage /></ProtectedRoute>} />
      <Route path="/redeem" element={<ProtectedRoute><RedeemPage /></ProtectedRoute>} />
      <Route path="/planner" element={<ProtectedRoute><PlannerPage /></ProtectedRoute>} />
      <Route path="/decisions" element={<ProtectedRoute><DecisionsPage /></ProtectedRoute>} />

      {/* Coach Routes */}
      <Route path="/coach" element={<ProtectedRoute allowedRoles={["coach", "admin"]}><CoachDashboard /></ProtectedRoute>} />

      {/* Venue Owner Routes */}
      <Route path="/venue" element={<ProtectedRoute allowedRoles={["venue_owner", "admin"]}><VenueDashboard /></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/fraud" element={<ProtectedRoute requiredRole="admin"><FraudSignalsPage /></ProtectedRoute>} />
      <Route path="/admin/agents" element={<ProtectedRoute requiredRole="admin"><AgentLogsPage /></ProtectedRoute>} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <AppRoutes />
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
